import { CartItemsComponent } from './component-cart-items.js';
import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

let changingVariant = false;
const root = () => window.Shopify.routes.root;
const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
const matches = (item, variantId, properties) => String(item.variant_id) === String(variantId) && propertiesKey(item.properties) === propertiesKey(properties) && !item.selling_plan_allocation;

async function request(path, body) {
  const response = await fetch(root() + path, {
    method: body ? 'POST' : 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json();
  if (!response.ok || data.errors || data.status >= 400) {
    throw new Error(typeof data.description === 'string' ? data.description : 'Cart update failed');
  }
  return data;
}

class RavageCartOptions extends HTMLElement {
  connectedCallback() {
    if (this.abort) return;
    this.abort = new AbortController();
    this.variants = JSON.parse(this.dataset.variants);
    this.selects = [...this.querySelectorAll('select')];
    this.button = this.querySelector('button');
    this.status = this.querySelector('[role="status"]');
    this.addEventListener('change', () => this.updateState(), { signal: this.abort.signal });
    this.button.addEventListener('click', () => this.save(), { signal: this.abort.signal });
    this.updateState();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.abort = null;
  }

  selectedVariant() {
    return this.variants.find((variant) => variant.options.every((value, index) => value === this.selects[index].value));
  }

  updateState() {
    const variant = this.selectedVariant();
    const changed = variant && String(variant.id) !== this.dataset.variantId;
    this.button.disabled = changingVariant || !variant?.available || !changed;
    this.status.textContent = variant?.available ? '' : this.dataset.unavailable;
    for (const select of this.selects) {
      const index = Number(select.dataset.optionIndex);
      for (const option of select.options) {
        option.disabled = !this.variants.some((candidate) => candidate.available && candidate.options[index] === option.value);
      }
    }
  }

  async save() {
    const replacement = this.selectedVariant();
    const owner = this.closest('cart-items-component');
    if (changingVariant || !replacement?.available || !owner) return;
    changingVariant = true;
    const errorMessage = this.dataset.error;
    const oldId = this.dataset.variantId;
    const oldKey = this.dataset.key;
    const storedProperties = JSON.parse(this.dataset.properties || '{}') || {};
    const properties = Array.isArray(storedProperties) ? Object.fromEntries(storedProperties) : storedProperties;
    const controls = [...document.querySelectorAll('cart-items-component button, cart-items-component select, cart-items-component input, button[name="checkout"]')];
    const disabledBefore = controls.map((control) => control.disabled);
    const accelerated = [...document.querySelectorAll('shopify-accelerated-checkout-cart')];
    const inertBefore = accelerated.map((element) => element.inert);
    accelerated.forEach((element) => { element.inert = true; });
    controls.forEach((control) => { control.disabled = true; });
    this.button.textContent = this.dataset.saving;
    this.status.textContent = this.dataset.saving;
    this.setAttribute('aria-busy', 'true');
    const blockCheckout = (event) => {
      if (changingVariant && event.target.closest?.('form[action*="/cart"]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    document.addEventListener('submit', blockCheckout, true);
    const deferred = CartLinesUpdateEvent.createPromise();
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((component) => component.dataset.sectionId))].slice(0, 5);
    const ownerSection = owner.dataset.sectionId;
    const mode = owner.hasAttribute('data-drawer') ? 'hydration' : 'full';
    let original;
    let baseline = 0;
    let attemptedAdd = false;
    let removed = false;
    const publish = async (cart, didError = false) => {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections, items: cart.items, itemCount: cart.item_count, source: 'ravage-cart-options', didError },
      });
      const html = cart.sections?.[ownerSection];
      sectionRenderer.abortRender(ownerSection);
      if (html) await morphSection(ownerSection, html, { mode });
      else await sectionRenderer.renderSection(ownerSection, { cache: false, mode });
      document.querySelectorAll('cart-quantity-selector-component').forEach((selector) => selector.updateButtonStates?.());
    };
    try {
      await new Promise((resolve) => setTimeout(resolve, 350));
      await Promise.allSettled(CartItemsComponent.pendingQuantityUpdates);
      const before = await request('cart.js');
      original = before.items.find((item) => item.key === oldKey) || before.items.find((item) => matches(item, oldId, properties));
      if (!original || original.selling_plan_allocation || original.parent_relationship) throw new Error(errorMessage);
      baseline = before.items.filter((item) => matches(item, replacement.id, original.properties)).reduce((total, item) => total + item.quantity, 0);
      owner.dispatchEvent(new CartLinesUpdateEvent({
        action: 'update', context: 'cart',
        lines: [{ id: original.key, quantity: original.quantity }],
        promise: deferred.promise,
      }));
      attemptedAdd = true;
      await request('cart/add.js', { items: [{ id: replacement.id, quantity: original.quantity, properties: original.properties || {} }] });
      const afterAdd = await request('cart.js');
      const oldLine = afterAdd.items.find((item) => matches(item, oldId, original.properties));
      if (!oldLine) throw new Error(errorMessage);
      const updated = await request('cart/change.js', {
        id: oldLine.key, quantity: 0, sections: sections.join(','), sections_url: window.location.pathname,
      });
      removed = true;
      await publish(updated);
    } catch (error) {
      let recovered;
      let completed = removed;
      try {
        recovered = await request('cart.js');
        const oldLine = original && recovered.items.find((item) => matches(item, oldId, original.properties));
        if (attemptedAdd && original && !oldLine) {
          completed = true;
        } else if (attemptedAdd && oldLine) {
          const replacementLine = recovered.items.find((item) => matches(item, replacement.id, original.properties));
          if (replacementLine && replacementLine.quantity > baseline) {
            recovered = await request('cart/change.js', { id: replacementLine.key, quantity: baseline });
          }
        }
        await publish(recovered, !completed);
      } catch {
        deferred.resolve({ detail: { didError: true, source: 'ravage-cart-options' } });
      }
      if (!completed) {
        const alert = document.createElement('p');
        alert.className = 'ravage-cart-update-error';
        alert.setAttribute('role', 'alert');
        alert.textContent = errorMessage;
        owner.querySelector('.ravage-cart-update-error')?.remove();
        owner.prepend(alert);
      }
    } finally {
      changingVariant = false;
      accelerated.forEach((element, index) => { if (element.isConnected) element.inert = inertBefore[index]; });
      document.removeEventListener('submit', blockCheckout, true);
      controls.forEach((control, index) => { if (control.isConnected) control.disabled = disabledBefore[index]; });
      document.querySelectorAll('ravage-cart-options').forEach((editor) => {
        editor.removeAttribute('aria-busy');
        editor.button.textContent = editor.dataset.update;
        editor.updateState();
      });
    }
  }
}

if (!customElements.get('ravage-cart-options')) {
  customElements.define('ravage-cart-options', RavageCartOptions);
}
